import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';
import ModuleProtectedRoute from '../components/ModuleProtectedRoute';
import { assistanceAPI, contributionsAPI, usersAPI } from '@/services';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { useToast } from '../components/Toast';
import PageHeader from '../components/PageHeader';
import {
  MemberMobileShell,
  MemberMobileHero,
} from '../components/MemberMobileShell';
import Pagination from '../components/Pagination';
import { isAdhesionContributionMonth } from '../utils/memberDashboardPersonalStats';
import { isMemberLikeEntreaUser } from '../utils/iamRoles';

/** Tailles proposées pour le relevé membre (25 conservé comme défaut historique). */
const MEMBER_TX_PAGE_SIZES = [10, 20, 25, 50, 100];

const CASE_STATUS_FR = {
  draft: 'Brouillon',
  submitted: 'Soumis',
  in_review: 'En examen',
  approved: 'Approuvé',
  rejected: 'Rejeté',
  paid: 'Payé',
  closed: 'Clôturé',
};

const EVENT_LABELS = {
  deces: 'Décès',
  hospitalisation: 'Hospitalisation',
  maladie: 'Maladie grave',
  naissance: 'Naissance',
  mariage: 'Mariage',
  catastrophe: 'Catastrophe / sinistre',
  autre: 'Autre',
};

function normalizeEventKey(s) {
  return String(s || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/** Libellé FR de la catégorie d’événement (ex. décès → Décès). */
function eventCategoryTitle(c) {
  const key = normalizeEventKey(c?.event_category).replace(/ç/g, 'c');
  if (key && EVENT_LABELS[key]) return EVENT_LABELS[key];
  const raw = String(c?.event_category || '').trim();
  if (raw) {
    return raw.charAt(0).toLocaleUpperCase('fr-FR') + raw.slice(1).toLocaleLowerCase('fr-FR');
  }
  return '';
}

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

function memberIdFromContribution(c) {
  return String(
    c?.person_id ||
      c?.member_id ||
      c?.person?.id ||
      c?.member?.id ||
      c?.metadata?.person_id ||
      c?.metadata?.member_id ||
      ''
  ).trim();
}

function monthSortKey(month) {
  const s = String(month || '').trim();
  if (/^\d{4}-\d{2}/.test(s)) return s.slice(0, 7);
  return s;
}

function dateFromMonth(month) {
  const s = String(month || '').trim();
  const p = s.split('-');
  if (p.length >= 2) {
    const d = new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, 1);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/** Heure de l’opération (du plus ancien au plus récent pour le cumul de solde). */
function caseSortDate(c) {
  const raw =
    c?.paid_at || c?.updated_at || c?.created_at || c?.createdAt || c?.submitted_at;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return 0;
}

function contributionChronoKey(c) {
  const raw = c.paid_at || c.updated_at || c.created_at;
  if (raw) {
    const t = new Date(raw).getTime();
    if (Number.isFinite(t)) return t;
  }
  const m = String(c.month || '').trim();
  if (/^\d{4}-\d{2}/.test(m)) {
    const d = new Date(`${m.slice(0, 7)}-01T12:00:00`);
    if (!Number.isNaN(d.getTime())) return d.getTime();
  }
  return 0;
}

function allocationLineAmount(c) {
  const st = String(c?.status || '').toLowerCase();
  if (st === 'rejected' || st === 'draft') return 0;
  if (st === 'closed') {
    const p = num(c.paid_amount);
    return p > 0 ? p : num(c.approved_amount);
  }
  if (st === 'paid') return num(c.paid_amount) || num(c.approved_amount);
  if (st === 'approved') return num(c.approved_amount);
  return 0;
}

/** Lignes « relevé » : uniquement opérations comptabilisées (payées / versées). */
function isCotisationComptabilisee(c) {
  return String(c?.status || '').toLowerCase() === 'completed';
}

function isAllocationComptabilisee(c) {
  const st = String(c?.status || '').toLowerCase();
  if (st !== 'paid' && st !== 'closed') return false;
  return allocationLineAmount(c) > 0;
}

function allocationDisplayDate(c) {
  const raw = c?.paid_at || c?.updated_at || c?.created_at || c?.createdAt;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

function formatCotisationPeriodLabel(month) {
  if (isAdhesionContributionMonth(month)) return 'Adhésion (entrée au groupe)';
  const s = String(month || '').trim();
  if (/^\d{4}-\d{2}$/.test(s)) {
    try {
      return new Date(`${s}-01T12:00:00`).toLocaleDateString('fr-FR', {
        month: 'long',
        year: 'numeric',
      });
    } catch {
      return s;
    }
  }
  return s || '—';
}

/** Date d’affichage relevé (adhésion : date réelle d’enregistrement, sinon mois de cotisation). */
function contributionDisplayDate(c) {
  if (isAdhesionContributionMonth(c?.month)) {
    const raw = c?.paid_at || c?.updated_at || c?.created_at;
    if (raw) {
      const d = new Date(raw);
      if (!Number.isNaN(d.getTime())) return d;
    }
    return null;
  }
  const fromMonth = dateFromMonth(c?.month);
  if (fromMonth && !Number.isNaN(fromMonth.getTime())) return fromMonth;
  const raw = c?.paid_at || c?.updated_at || c?.created_at;
  if (raw) {
    const d = new Date(raw);
    if (!Number.isNaN(d.getTime())) return d;
  }
  return null;
}

/**
 * Page membre : historique type « Transaction History » (relevé) pour cotisations ou allocations.
 * Même en-tête / cartes de synthèse que l’espace d’origine, avec tableau Date · Description · Montant · Solde.
 */
const MemberTransactionHistory = ({ mode }) => {
  const { user } = useAuth();
  const { role, hasPermission } = usePermissions();
  const { error: toastError } = useToast();
  const hasMemberProfileLink = !!(user?.member_id || user?.person_id);
  const isMembre = isMemberLikeEntreaUser(role, hasMemberProfileLink);
  const isAlloc = mode === 'allocations';

  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [mechanismById, setMechanismById] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [resolvedPersonId, setResolvedPersonId] = useState('');
  const [itemsPerPage, setItemsPerPage] = useState(25);

  const currentPersonId = String(user?.person_id || user?.member_id || '').trim();
  const effectivePersonId = currentPersonId || resolvedPersonId;

  const copy = isAlloc
    ? {
        title: 'Détails des transactions — allocations',
        subtitle: 'Relevé des seules allocations comptabilisées (versées : payé ou clôture avec montant), avec solde cumulé.',
        mobileKicker: 'Allocations',
        mobileTitle: 'Historique des allocations',
        mobileDesc: 'Uniquement les versements comptabilisés, comme un relevé bancaire.',
        backHref: '/assistance',
        backLabel: "Retour aux allocations",
        cta: '+ Nouvelle demande',
        ctaHref: '/assistance',
        stat1: { k: 'Total comptabilisé', s: 'Somme des versements reçus' },
        stat2: { k: 'Opérations', s: 'Dossiers versés' },
        stat3: { k: 'Moyenne', s: 'Par versement' },
        stat4: { k: 'Avec pièces', s: 'Justificatifs' },
      }
    : {
        title: 'Détails des transactions — cotisations',
        subtitle: 'Relevé des seules cotisations comptabilisées (statut payée), avec solde cumulé.',
        mobileKicker: 'Cotisations',
        mobileTitle: 'Historique des cotisations',
        mobileDesc: 'Uniquement les cotisations comptabilisées ; recherche sur ce relevé.',
        backHref: '/contributions',
        backLabel: 'Retour aux cotisations',
        cta: '+ Ajouter ma cotisation',
        ctaHref: '/contributions',
        stat1: { k: 'Total comptabilisé', s: "Somme des versements reconnus" },
        stat2: { k: 'Opérations', s: "Nombre d'écritures" },
        stat3: { k: 'Moyenne', s: 'Par opération' },
        stat4: { k: 'Avec justificatif', s: 'Pièces' },
      };

  const canCreateContrib = hasPermission('contributions.create') || hasPermission('contributions.create_own');
  const canCreateAlloc =
    hasPermission('assistance.cases.create') || hasPermission('contributions.create');

  useEffect(() => {
    const run = async () => {
      if (isMembre && !currentPersonId) {
        const ids = [user?.id, user?.sub, user?.user_id].map((v) => String(v || '').trim()).filter(Boolean);
        for (const uid of ids) {
          try {
            const res = await usersAPI.getById(uid);
            const pid = String(res?.data?.person_id || res?.data?.member_id || '').trim();
            if (pid) {
              setResolvedPersonId(pid);
              return;
            }
          } catch {
            /* try next */
          }
        }
      }
    };
    run();
  }, [isMembre, currentPersonId, user?.id, user?.sub, user?.user_id]);

  const load = useCallback(async () => {
    if (!isMembre || !effectivePersonId) {
      setRows([]);
      setLoading(false);
      return;
    }
    try {
      setLoading(true);
      if (isAlloc) {
        const [mRes, cRes] = await Promise.all([
          assistanceAPI.getMechanisms({ limit: 200, skip: 0 }),
          assistanceAPI.getCases({ limit: 200, skip: 0, member_profile_id: effectivePersonId }),
        ]);
        const map = {};
        (mRes?.data?.solidarity_mechanisms || []).forEach((m) => {
          if (m?.id) map[m.id] = m;
        });
        setMechanismById(map);
        setRows(Array.isArray(cRes?.data?.assistance_cases) ? cRes.data.assistance_cases : []);
      } else {
        const cRes = await contributionsAPI.getAll({ limit: 2000, skip: 0, person_id: effectivePersonId });
        const list = Array.isArray(cRes.data) ? cRes.data : cRes.data?.contributions || [];
        setRows(list.filter((c) => memberIdFromContribution(c) === effectivePersonId));
      }
    } catch (e) {
      console.error(e);
      toastError('Chargement impossible.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [isMembre, effectivePersonId, isAlloc, toastError]);

  useEffect(() => {
    load();
  }, [load]);

  const accountedRows = useMemo(() => {
    if (isAlloc) return rows.filter(isAllocationComptabilisee);
    return rows.filter(isCotisationComptabilisee);
  }, [rows, isAlloc]);

  const stats = useMemo(() => {
    const list = accountedRows;
    if (isAlloc) {
      const n = list.length;
      const totalVersé = list.reduce((s, c) => s + allocationLineAmount(c), 0);
      const moy = n > 0 ? Math.round(totalVersé / n) : 0;
      const avecPièces = list.filter(
        (c) => Array.isArray(c?.supporting_file_ids) && c.supporting_file_ids.length > 0
      ).length;
      return { v1: totalVersé, v2: n, v3: moy, v4: avecPièces, isAmount1: true, v3IsAmount: true };
    }
    const n = list.length;
    const totalAmt = list.reduce((s, c) => s + num(c?.amount), 0);
    const moy = n > 0 ? Math.round(totalAmt / n) : 0;
    const justif = list.filter((c) => Array.isArray(c?.proof_documents) && c.proof_documents.length > 0).length;
    return { v1: totalAmt, v2: n, v3: moy, v4: justif, isAmount1: true, v3IsAmount: true };
  }, [accountedRows, isAlloc]);

  const tableRows = useMemo(() => {
    let base = [...accountedRows];
    if (!isAlloc) {
      base.sort(
        (a, b) =>
          contributionChronoKey(a) - contributionChronoKey(b) ||
          monthSortKey(a.month).localeCompare(monthSortKey(b.month)) ||
          String(a.id).localeCompare(String(b.id))
      );
    } else {
      base.sort((a, b) => caseSortDate(a) - caseSortDate(b) || String(a.id).localeCompare(String(b.id)));
    }

    const q = String(search || '').trim().toLowerCase();
    if (q) {
      base = base.filter((c) => {
        if (isAlloc) {
          return (
            String(c.id || '')
              .toLowerCase()
              .includes(q) ||
            String(c.reason || '')
              .toLowerCase()
              .includes(q) ||
            String(c.event_category || '')
              .toLowerCase()
              .includes(q)
          );
        }
        return (
          String(c.id || '')
            .toLowerCase()
            .includes(q) ||
          String(c.month || '')
            .toLowerCase()
            .includes(q) ||
          String(c.notes || '')
            .toLowerCase()
            .includes(q)
        );
      });
    }

    let running = 0;
    if (isAlloc) {
      const forward = base.map((c) => {
        const add = allocationLineAmount(c);
        running += add;
        const m = mechanismById[c.mechanism_id];
        const st = String(c?.status || '').toLowerCase();
        const eventTitle = eventCategoryTitle(c);
        const mechName = m?.name ? String(m.name).trim() : '';
        const labelName = eventTitle
          ? `Allocation ${eventTitle}`
          : mechName || 'Allocation';
        const reasonLine = c.reason
          ? `${String(c.reason).slice(0, 120)}${String(c.reason).length > 120 ? '…' : ''}`
          : '';
        const subParts = [mechName, reasonLine].filter(Boolean);
        const labelSecondary = subParts.length
          ? subParts.join(' — ')
          : 'Comptabilisé — solde de solidarité';
        return {
          raw: c,
          add,
          running,
          labelType: 'Dépôt',
          labelName,
          labelSecondary,
          statusFr: CASE_STATUS_FR[st] || st,
        };
      });
      forward.reverse();
      return forward;
    }

    const forwardC = base.map((c) => {
      const amt = num(c?.amount);
      running += amt;
      const isAdh = isAdhesionContributionMonth(c.month);
      return {
        raw: c,
        add: amt,
        running,
        labelType: 'Dépôt',
        labelName: isAdh ? "Frais d'adhésion" : 'Cotisation',
        labelSecondary: `Comptabilisé — ${formatCotisationPeriodLabel(c.month)}`,
      };
    });
    forwardC.reverse();
    return forwardC;
  }, [accountedRows, search, isAlloc, mechanismById]);

  const totalPages = Math.max(1, Math.ceil(tableRows.length / itemsPerPage));
  const paged = useMemo(() => {
    const start = (page - 1) * itemsPerPage;
    return tableRows.slice(start, start + itemsPerPage);
  }, [tableRows, page, itemsPerPage]);

  useEffect(() => {
    setPage(1);
  }, [search, mode]);

  useEffect(() => {
    setPage(1);
  }, [itemsPerPage]);

  if (!isMembre) {
    return <Navigate to={isAlloc ? '/assistance' : '/contributions'} replace />;
  }

  if (!loading && !effectivePersonId) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
        Profil membre non associé.{' '}
        <Link to="/support" className="font-semibold text-amber-950 underline">
          Contactez le support
        </Link>
        .
      </div>
    );
  }

  return (
    <div className="min-h-[60vh] space-y-4 sm:space-y-5">
      <div className="hidden md:flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <Link
          to={copy.backHref}
          className="inline-flex text-sm font-semibold text-[#113a5b] hover:underline w-fit"
        >
          ← {copy.backLabel}
        </Link>
        {(isAlloc ? canCreateAlloc : canCreateContrib) && (
          <Link
            to={copy.ctaHref}
            className="inline-flex min-h-[40px] items-center justify-center rounded-lg bg-[#113a5b] px-4 text-sm font-semibold text-white shadow-sm hover:bg-[#0f3350]"
          >
            {copy.cta}
          </Link>
        )}
      </div>

      <div className="hidden md:block">
        <PageHeader title={copy.title} subtitle={copy.subtitle} />
      </div>

      <MemberMobileShell>
        <MemberMobileHero
          label={copy.mobileKicker}
          title={
            !loading
              ? isAlloc
                ? String(stats.v1)
                : `${Number(stats.v1 || 0).toLocaleString('fr-FR')} FBU`
              : '…'
          }
          hint={copy.stat1?.k}
          tone={Number(stats.v3) > 0 ? 'warn' : 'ok'}
          actions={
            <>
              <Link
                to={copy.backHref}
                className="mm-cta mm-cta--ghost flex-1"
              >
                ← {copy.backLabel}
              </Link>
              {(isAlloc ? canCreateAlloc : canCreateContrib) && (
                <Link
                  to={copy.ctaHref}
                  className="mm-cta flex-1"
                >
                  {copy.cta}
                </Link>
              )}
            </>
          }
        >
          {!loading ? (
            <p className="mt-2 text-xs text-slate-600">
              {copy.stat2?.k}: <span className="font-semibold">{stats.v2}</span>
              {' · '}
              {copy.stat3?.k}: <span className="font-semibold">{stats.v3}</span>
            </p>
          ) : null}
        </MemberMobileHero>
      </MemberMobileShell>

      {!loading && (
        <section className="hidden md:grid gap-2.5 sm:grid-cols-2 xl:grid-cols-4">
          <article className="mm-card px-3 py-2.5">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{copy.stat1.k}</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-[#0f3350]">
              {isAlloc ? (
                stats.v1.toLocaleString('fr-FR')
              ) : (
                <>
                  {stats.v1.toLocaleString('fr-FR')}
                  <span className="ml-1 align-top text-[10px] font-semibold text-slate-600">FBU</span>
                </>
              )}
            </p>
            <p className="mt-0.5 text-[10px] text-slate-600">{copy.stat1.s}</p>
          </article>
          <article className="rounded-2xl border-l-4 border-emerald-500 border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-emerald-700">{copy.stat2.k}</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-[#0f3350]">{stats.v2}</p>
            <p className="mt-0.5 text-[10px] text-slate-600">{copy.stat2.s}</p>
          </article>
          <article className="rounded-2xl border-l-4 border-amber-400 border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-amber-800">{copy.stat3.k}</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-[#0f3350]">
              {stats.v3.toLocaleString('fr-FR')}
              {stats.v3IsAmount && (
                <span className="ml-1 align-top text-[10px] font-semibold text-slate-600">FBU</span>
              )}
            </p>
            <p className="mt-0.5 text-[10px] text-slate-600">{copy.stat3.s}</p>
          </article>
          <article className="rounded-2xl border-l-4 border-[#2f6fd4] border border-slate-200/80 bg-white px-3 py-2.5 shadow-sm">
            <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-600">{copy.stat4.k}</p>
            <p className="mt-1 text-lg font-bold tabular-nums text-[#0f3350]">{stats.v4}</p>
            <p className="mt-0.5 text-[10px] text-slate-600">{copy.stat4.s}</p>
          </article>
        </section>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <div>
          <label className="mb-1 block text-xs font-semibold uppercase tracking-wide text-slate-600">
            Recherche
          </label>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isAlloc ? 'ID, motif, catégorie…' : 'Mois, notes, référence…'}
            className="w-full max-w-2xl rounded-lg border border-slate-300 px-3 py-2.5 text-sm"
          />
        </div>
        <p className="mt-3 text-xs text-slate-500">{loading ? 'Chargement…' : null}</p>
      </div>

      {loading ? (
        <div className="mm-card flex flex-col items-center justify-center py-16">
          <div className="h-11 w-11 animate-spin rounded-full border-2 border-[#113a5b] border-t-transparent" />
          <p className="mt-4 text-sm text-slate-600">Construction du relevé…</p>
        </div>
      ) : (
        <>
          {tableRows.length > 0 && (
            <Pagination
              placement="top"
              currentPage={page}
              totalPages={totalPages}
              totalItems={tableRows.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
              onPageSizeChange={setItemsPerPage}
              pageSizeOptions={MEMBER_TX_PAGE_SIZES}
            />
          )}
          <div className="hidden md:block overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 bg-slate-100/90 text-left text-xs font-semibold uppercase tracking-wide text-slate-600">
                  <th className="whitespace-nowrap px-3 py-3 w-[120px]">Date</th>
                  <th className="px-3 py-3">Description</th>
                  <th className="whitespace-nowrap px-3 py-3 text-right">Montant</th>
                  <th className="whitespace-nowrap px-3 py-3 text-right">Solde</th>
                  <th className="w-10 px-1 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {paged.map((r) => {
                  const d = isAlloc ? allocationDisplayDate(r.raw) : contributionDisplayDate(r.raw);
                  const validD = d && !Number.isNaN(d.getTime());
                  const dayNum = validD ? d.getDate() : '—';
                  const mon = validD ? d.toLocaleDateString('fr-FR', { month: 'short' }) : '—';
                  const year = validD ? d.getFullYear() : '—';
                  const showAmt = isAlloc ? allocationLineAmount(r.raw) : num(r.raw?.amount);
                  return (
                    <tr key={String(r.raw?.id)} className="hover:bg-slate-50/80">
                      <td className="px-3 py-3 align-top text-slate-800">
                        <p className="text-[10px] font-medium uppercase text-slate-500">
                          {mon} {year}
                        </p>
                        <div className="mt-1 inline-flex min-w-[2.5rem] items-center justify-center rounded border border-slate-200 bg-slate-50 px-1.5 py-0.5 text-sm font-semibold text-[#0f3350]">
                          {validD && dayNum === 1 ? '1er' : dayNum}
                        </div>
                      </td>
                      <td className="px-3 py-3 align-top">
                        <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                          {r.labelType}
                        </p>
                        <p className="mt-0.5 font-semibold text-[#0f3350]">{r.labelName}</p>
                        <p className="mt-0.5 text-xs text-slate-500 line-clamp-2">
                          {r.labelSecondary}
                          {isAlloc && r.statusFr ? ` — ${r.statusFr}` : null}
                        </p>
                        {!isAlloc && r.raw?.id ? (
                          <p className="mt-0.5 font-mono text-[10px] text-slate-400">ID {r.raw.id}</p>
                        ) : null}
                        {isAlloc && r.raw?.id ? (
                          <p className="mt-0.5 font-mono text-[10px] text-slate-400">{r.raw.id}</p>
                        ) : null}
                      </td>
                      <td
                        className={`px-3 py-3 text-right font-semibold tabular-nums ${
                          isAlloc ? (showAmt > 0 ? 'text-emerald-700' : 'text-slate-500') : 'text-emerald-700'
                        }`}
                      >
                        {isAlloc ? (
                          showAmt > 0 ? (
                            <>+{showAmt.toLocaleString('fr-FR')} FBU</>
                          ) : (
                            <span className="text-slate-500">—</span>
                          )
                        ) : (
                          `${num(r.raw?.amount).toLocaleString('fr-FR')} FBU`
                        )}
                      </td>
                      <td className="px-3 py-3 text-right text-sm font-semibold tabular-nums text-[#0f3350]">
                        {r.running.toLocaleString('fr-FR')} FBU
                      </td>
                      <td className="px-1 py-3 text-slate-300" aria-hidden>
                        ›
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {tableRows.length === 0 && (
              <p className="px-4 py-10 text-center text-sm text-slate-500">
                Aucune opération comptabilisée pour l’instant{search.trim() ? ' (essayez un autre critère de recherche).' : '.'}
              </p>
            )}
          </div>

          <div className="space-y-3 md:hidden">
            {paged.map((r) => {
              const d = isAlloc
                ? allocationDisplayDate(r.raw) || (r.raw?.created_at ? new Date(r.raw.created_at) : null)
                : contributionDisplayDate(r.raw);
              const dOk = d && !Number.isNaN(d.getTime());
              const showAmt = isAlloc ? allocationLineAmount(r.raw) : num(r.raw?.amount);
              return (
                <div
                  key={isAlloc ? r.raw.id : r.raw.id}
                  className="mm-card p-4 border-l-4 border-l-[#2f6fd4]"
                >
                  <p className="text-[10px] font-medium uppercase text-slate-500">
                    {dOk
                      ? d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' })
                      : '—'}
                  </p>
                  <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">{r.labelType}</p>
                  <p className="mt-0.5 text-base font-semibold text-[#0f3350]">{r.labelName}</p>
                  <p className="text-xs text-slate-600 mt-0.5">
                    {r.labelSecondary}
                    {isAlloc && r.statusFr ? ` — ${r.statusFr}` : null}
                  </p>
                  <div className="mt-2 flex items-center justify-between">
                    <span
                      className={`text-sm font-bold tabular-nums ${
                        isAlloc ? (showAmt > 0 ? 'text-emerald-700' : 'text-slate-500') : 'text-emerald-700'
                      }`}
                    >
                      {isAlloc
                        ? showAmt > 0
                          ? `+${showAmt.toLocaleString('fr-FR')} FBU`
                          : '—'
                        : `${showAmt.toLocaleString('fr-FR')} FBU`}
                    </span>
                    <span className="text-xs text-slate-500">
                      Solde cumulé{' '}
                      <span className="font-semibold text-[#0f3350]">{r.running.toLocaleString('fr-FR')} FBU</span>
                    </span>
                  </div>
                </div>
              );
            })}
            {tableRows.length === 0 && (
              <p className="text-center text-sm text-slate-500">
                Aucune opération comptabilisée pour l’instant{search.trim() ? ' (essayez un autre critère de recherche).' : '.'}
              </p>
            )}
          </div>

          {tableRows.length > 0 && (
            <Pagination
              placement="bottom"
              currentPage={page}
              totalPages={totalPages}
              totalItems={tableRows.length}
              itemsPerPage={itemsPerPage}
              onPageChange={setPage}
              onPageSizeChange={setItemsPerPage}
              pageSizeOptions={MEMBER_TX_PAGE_SIZES}
            />
          )}
        </>
      )}
    </div>
  );
};

/** Route unifiée : `/transactionDetails/cotisations` ou `/transactionDetails/allocations`. */
export function MemberTransactionDetailsRoute() {
  const { mode } = useParams();
  if (mode === 'cotisations') {
    return (
      <ModuleProtectedRoute route="/contributions">
        <MemberTransactionHistory mode="cotisations" />
      </ModuleProtectedRoute>
    );
  }
  if (mode === 'allocations') {
    return (
      <ModuleProtectedRoute route="/support">
        <MemberTransactionHistory mode="allocations" />
      </ModuleProtectedRoute>
    );
  }
  return <Navigate to="/dashboard" replace />;
}

export default MemberTransactionHistory;
